import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Badge } from '@/components/ui/Badge';
import { Empty } from '@/components/ui/Empty';
import { useParentVisibleAssessments } from '@/data/assessments';

export const Route = createFileRoute('/parent/progress')({
  component: ParentProgress,
});

function formatSqlDate(value: string) {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return value;
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(year, month - 1, day));
}

function dancerName(dancer: {
  first_name: string;
  last_name: string;
  preferred_name: string | null;
}) {
  return `${dancer.preferred_name ?? dancer.first_name} ${dancer.last_name}`;
}

function formatScore(value: string | number | boolean | undefined) {
  if (value === undefined) return 'Not scored';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value).replaceAll('_', ' ');
}

function ParentProgress() {
  const assessments = useParentVisibleAssessments();
  const assessmentRows = assessments.data;
  const grouped = useMemo(() => {
    const rows = assessmentRows ?? [];
    const groups = new Map<string, typeof rows>();
    for (const assessment of rows) {
      const key = assessment.dancer_id;
      groups.set(key, [...(groups.get(key) ?? []), assessment]);
    }
    return [...groups.entries()];
  }, [assessmentRows]);

  return (
    <div className="space-y-10">
      <header>
        <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
          Report cards
        </p>
        <h1 className="mt-2">Progress</h1>
      </header>

      {assessments.isLoading && <p aria-live="polite">Loading...</p>}

      {grouped.length === 0 && !assessments.isLoading ? (
        <Empty
          title="No progress reports yet"
          description="Parent-visible assessments from your dancer's faculty will appear here."
        />
      ) : (
        <div className="space-y-8">
          {grouped.map(([dancerId, dancerAssessments]) => {
            const dancer = dancerAssessments[0]?.dancers;
            return (
              <section key={dancerId} aria-labelledby={`dancer-${dancerId}`} className="space-y-4">
                <h2 id={`dancer-${dancerId}`} className="text-[var(--text-xl)]">
                  {dancer ? dancerName(dancer) : 'Dancer'}
                </h2>

                <ul className="grid gap-4">
                  {dancerAssessments.map((assessment) => {
                    const criteria = assessment.assessment_templates?.criteria ?? [];
                    return (
                      <li key={assessment.id} className="surface-elevated p-5">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <h3 className="font-[var(--font-display)] text-[var(--text-xl)]">
                              {assessment.assessment_templates?.name ?? 'Progress report'}
                            </h3>
                            <p className="mt-1 text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                              {formatSqlDate(assessment.assessed_on)}
                              {assessment.classes?.name ? ` · ${assessment.classes.name}` : ''}
                            </p>
                          </div>
                          <Badge tone="success">shared</Badge>
                        </div>

                        {assessment.summary && (
                          <p className="mt-4 leading-[var(--leading-relaxed)]">
                            {assessment.summary}
                          </p>
                        )}

                        <dl className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                          {criteria.map((criterion) => (
                            <div
                              key={criterion.key}
                              className="rounded-[var(--radius-md)] border border-[var(--color-border)] p-3"
                            >
                              <dt className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                                {criterion.label}
                              </dt>
                              <dd className="mt-1 font-medium capitalize">
                                {formatScore(assessment.scores[criterion.key])}
                              </dd>
                            </div>
                          ))}
                        </dl>
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
